import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Ship, Bike, Car, X, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import useDocumentMeta from "@/lib/useDocumentMeta";
import { Reveal } from "@/lib/reveal";

const SITE_URL = "https://juketransfers.hr";
const AUTOFILL_KEY = "juke_guest_contact";

const CATEGORY_META = {
  boats: { label: "Boats · Charter", Icon: Ship, image: "https://images.unsplash.com/photo-1552160757-52790c6f4faf?auto=format&fit=crop&w=1600&q=80" },
  quads_scooters: { label: "Quads & Scooters", Icon: Bike, image: "https://images.unsplash.com/photo-1648512464623-257373a144c5?auto=format&fit=crop&w=1600&q=80" },
  rent_a_car: { label: "Rent-a-Car", Icon: Car, image: "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?auto=format&fit=crop&w=1600&q=80" },
  tours: { label: "Guided Tours", Icon: Ship, image: "https://images.unsplash.com/photo-1552160757-52790c6f4faf?auto=format&fit=crop&w=1600&q=80" },
  activities: { label: "Activities", Icon: Bike, image: "https://images.unsplash.com/photo-1648512464623-257373a144c5?auto=format&fit=crop&w=1600&q=80" },
  other: { label: "Experiences", Icon: Ship, image: "https://images.unsplash.com/photo-1552160757-52790c6f4faf?auto=format&fit=crop&w=1600&q=80" },
};
const STATIC_TO_CATEGORY = { boat_charter: "boats", quad_scooter: "quads_scooters", rent_a_car: "rent_a_car" };

const SERVICES = [
  {
    code: "boat_charter",
    title: "Private Boat Charters",
    tagline: "Islands · Blue Cave · Pakleni",
    Icon: Ship,
    image:
      "https://images.unsplash.com/photo-1552160757-52790c6f4faf?auto=format&fit=crop&w=1600&q=80",
    description:
      "Explore the breathtaking islands, hidden caves, and Pakleni islands with a professional private speedboat or luxury yacht tour.",
    highlights: [
      "Skippered speedboats & flybridge yachts",
      "Bespoke Blue Cave · Hvar · Vis itineraries",
      "Champagne on board, on request",
    ],
  },
  {
    code: "quad_scooter",
    title: "Quad & Scooter Rentals",
    tagline: "Skip the summer traffic",
    Icon: Bike,
    image:
      "https://images.unsplash.com/photo-1648512464623-257373a144c5?auto=format&fit=crop&w=1600&q=80",
    description:
      "Skip the summer traffic or go on an off-road adventure. Premium Vespas and rugged Quads delivered directly to your location.",
    highlights: [
      "Vespa Primavera & GTS models",
      "500cc off-road quads with helmets",
      "Delivery to your hotel or villa",
    ],
  },
  {
    code: "rent_a_car",
    title: "Premium Rent-a-Car",
    tagline: "Wheels for your Dalmatia day trips",
    Icon: Car,
    image:
      "https://images.unsplash.com/photo-1592309905620-e5b59f6dcb98?auto=format&fit=crop&w=1600&q=80",
    description:
      "Need wheels for your independent day trips around Dalmatia? Get access to high-end, reliable vehicles through our trusted rental fleet partners.",
    highlights: [
      "Compact hybrids to full-size SUVs",
      "Unlimited kilometres in Croatia",
      "Free delivery at Split Airport",
    ],
  },
];

function loadAutofill() {
  try {
    const raw = sessionStorage.getItem(AUTOFILL_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export default function ExperiencesPage() {
  const [openService, setOpenService] = useState(null);
  const [dbServices, setDbServices] = useState([]);

  useEffect(() => {
    api.get("/experiences/services").then(({ data }) => setDbServices(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  // DB-driven cards when admin has live services; static fallback otherwise.
  const cards = useMemo(() => {
    if (dbServices.length > 0) {
      return dbServices.map((sv) => {
        const meta = CATEGORY_META[sv.category] || CATEGORY_META.other;
        return {
          code: sv.id,
          service_id: sv.id,
          category: sv.category,
          title: sv.title,
          tagline: meta.label,
          Icon: meta.Icon,
          image: sv.image_url || meta.image,
          description: sv.description || `Provided by our vetted partner ${sv.supplier_name}.`,
          highlights: [],
          price_from_eur: sv.price_from_eur,
        };
      });
    }
    return SERVICES.map((s) => ({ ...s, service_id: null, category: STATIC_TO_CATEGORY[s.code] || "other" }));
  }, [dbServices]);

  useDocumentMeta({
    title: "Experiences & Rentals in Split — Boats, Quads, Rent-a-Car · Juke Transfers",
    description:
      "Complete your Croatian journey. Book private boat charters, Vespa and quad rentals, and premium rent-a-car through Juke Transfers' curated Split partners.",
    canonical: `${SITE_URL}/experiences`,
    ogImage: SERVICES[0].image,
    ogType: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": cards.map((s, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "name": s.title,
        "url": `${SITE_URL}/experiences#${s.code}`,
      })),
    },
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-40 pb-20 px-6 lg:px-10 border-b border-white/5 overflow-hidden">
        {/* subtle gold radial */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(60% 60% at 20% 20%, rgba(197,160,89,0.12) 0%, rgba(0,0,0,0) 60%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#C5A059] mb-4">
            Experiences & Rentals
          </p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-4xl">
            Complete Your Journey
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-base leading-relaxed">
            Elevate your stay in Croatia. Beyond premium transfers, we partner with the finest
            local agencies to offer exclusive rentals and experiences directly to our guests.
          </p>
        </div>
      </section>

      {/* Cards */}
      <section className="flex-1 px-6 lg:px-10 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((s, i) => (
            <Reveal key={s.code} delay={i * 90}>
            <article
              id={s.code}
              data-testid={`experience-card-${s.code}`}
              className="group relative flex flex-col bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden transition-all duration-500 hover:border-[#C5A059]/50 hover:-translate-y-1 hover:shadow-[0_24px_80px_-24px_rgba(197,160,89,0.35)]"
            >
              <div className="relative aspect-[5/4] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                <div className="absolute top-4 left-4 h-10 w-10 rounded-full bg-black/50 backdrop-blur border border-[#C5A059]/40 flex items-center justify-center">
                  <s.Icon className="w-4 h-4 text-[#C5A059]" />
                </div>
              </div>

              <div className="p-6 lg:p-7 flex-1 flex flex-col">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059]">
                    {s.tagline}
                  </p>
                  {s.price_from_eur != null && (
                    <span data-testid={`price-from-${s.code}`} className="text-[11px] uppercase tracking-[0.15em] text-black bg-[#C5A059] rounded-full px-3 py-1 font-semibold shrink-0">
                      From €{Number(s.price_from_eur).toFixed(0)}
                    </span>
                  )}
                </div>
                <h3 className="mt-2 font-display text-2xl lg:text-3xl text-white font-light tracking-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                  {s.description}
                </p>

                <ul className="mt-4 space-y-1.5">
                  {s.highlights.map((h) => (
                    <li key={h} className="text-xs text-zinc-500 flex gap-2">
                      <span className="text-[#C5A059] shrink-0">·</span>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-6">
                  <button
                    type="button"
                    data-testid={`inquire-btn-${s.code}`}
                    onClick={() => setOpenService(s)}
                    className="w-full h-12 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[11px] uppercase tracking-[0.25em] font-semibold flex items-center justify-center gap-2 transition-all"
                  >
                    Inquire Now <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </article>
            </Reveal>
          ))}
        </div>

        {/* Trust footnote */}
        <p className="max-w-3xl mx-auto text-center text-xs uppercase tracking-[0.3em] text-zinc-600 mt-14">
          Curated partners · Vetted operators · One phone number for the whole trip
        </p>
      </section>

      <Footer />

      <InquiryDialog
        service={openService}
        onClose={() => setOpenService(null)}
      />
    </div>
  );
}

function InquiryDialog({ service, onClose }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", service_date: "", service_time: "", guests: "2", notes: "",
  });
  const [autofilled, setAutofilled] = useState(false);

  // On open: hydrate from sessionStorage (transfer form leaves contact behind).
  useEffect(() => {
    if (!service) return;
    const seed = loadAutofill() || {};
    setForm({
      name: seed.name || "",
      email: seed.email || "",
      whatsapp: seed.phone || seed.whatsapp || "",
      service_date: "",
      service_time: "",
      guests: "2",
      notes: "",
    });
    setAutofilled(!!(seed.name || seed.email || seed.phone));
  }, [service]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Persist contact tuple to sessionStorage on-blur so partial fills survive
  // across services + carry back to the main transfer form.
  const persistContact = () => {
    if (!form.name && !form.email && !form.whatsapp) return;
    try {
      sessionStorage.setItem(
        AUTOFILL_KEY,
        JSON.stringify({ name: form.name, email: form.email, phone: form.whatsapp }),
      );
    } catch { /* storage disabled */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return;
    if (!form.name || !form.email || !form.whatsapp) {
      toast.error("Please fill in your name, email and WhatsApp number.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/experiences/request", {
        service_id: service.service_id || null,
        category: service.category || "other",
        name: form.name.trim(),
        email: form.email.trim(),
        whatsapp: form.whatsapp.trim(),
        service_date: form.service_date || "",
        service_time: form.service_time || "",
        guests: Number(form.guests) || 0,
        notes: form.notes,
      });
      // Persist contact details for future autofill (transfer + other services).
      try {
        sessionStorage.setItem(
          AUTOFILL_KEY,
          JSON.stringify({ name: form.name, email: form.email, phone: form.whatsapp }),
        );
      } catch { /* storage disabled */ }
      toast.success("Request sent — no payment charged. You'll get a confirmation shortly.");
      onClose?.();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Could not send inquiry. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!service} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        data-testid="inquiry-dialog"
        className="bg-[#0a0a0a] border-white/10 text-zinc-100 max-w-lg p-0 overflow-hidden [&>button.absolute]:hidden"
      >
        {service && (
          <>
            <div className="relative h-40 w-full">
              <img
                src={service.image}
                alt={service.title}
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-transparent" />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                data-testid="inquiry-dialog-close"
                className="absolute top-3 right-3 h-9 w-9 rounded-full bg-black/60 border border-white/10 flex items-center justify-center hover:bg-black text-white"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="absolute bottom-4 left-5 right-5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] mb-1">
                  Inquire · {service.tagline}
                </p>
                <DialogHeader className="p-0 space-y-1">
                  <DialogTitle asChild>
                    <h2 className="font-display text-2xl text-white font-light tracking-tight">
                      {service.title}
                    </h2>
                  </DialogTitle>
                  <DialogDescription className="text-zinc-400 text-xs">
                    We&rsquo;ll reply by email or WhatsApp within a few hours.
                  </DialogDescription>
                </DialogHeader>
              </div>
            </div>

            <form onSubmit={submit} className="p-6 space-y-4">
              {autofilled && (
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#C5A059] flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#C5A059]" />
                  Autofilled from your transfer request
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Name">
                  <Input
                    data-testid="inquiry-name-input"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    onBlur={persistContact}
                    placeholder="Full name"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Email">
                  <Input
                    data-testid="inquiry-email-input"
                    required
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    onBlur={persistContact}
                    placeholder="you@example.com"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="WhatsApp">
                  <Input
                    data-testid="inquiry-whatsapp-input"
                    required
                    value={form.whatsapp}
                    onChange={(e) => update("whatsapp", e.target.value)}
                    onBlur={persistContact}
                    placeholder="+385 …"
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Date of service">
                  <Input
                    data-testid="inquiry-date-input"
                    type="date"
                    value={form.service_date}
                    onChange={(e) => update("service_date", e.target.value)}
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Preferred time">
                  <Input
                    data-testid="inquiry-time-input"
                    type="time"
                    value={form.service_time}
                    onChange={(e) => update("service_time", e.target.value)}
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
                <Field label="Guests">
                  <Input
                    data-testid="inquiry-guests-input"
                    type="number"
                    min="1"
                    value={form.guests}
                    onChange={(e) => update("guests", e.target.value)}
                    className="bg-zinc-900 border-white/10 h-11 text-sm"
                  />
                </Field>
              </div>

              <Field label="Message / notes">
                <Textarea
                  data-testid="inquiry-notes-input"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  placeholder="Party size, preferred itinerary, pick-up location…"
                  rows={4}
                  className="bg-zinc-900 border-white/10 text-sm"
                />
              </Field>

              <button
                type="submit"
                data-testid="inquiry-submit-btn"
                disabled={saving}
                className="w-full h-12 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black text-[11px] uppercase tracking-[0.25em] font-semibold flex items-center justify-center gap-2 transition-all"
              >
                <Send className="w-4 h-4" />
                {saving ? "Sending…" : "Send Inquiry"}
              </button>

              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-600 text-center">
                Handled by our vetted local partner · No payment now
              </p>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
